import { useAppDispatch, useAppSelector } from 'app/hooks';
import { syncHybridWholeHouseConfiguration } from 'features/configurations';
import useSystemDesignBypassMode from 'hooks/use-system-design-bypass-mode';
import { useEffect } from 'react';
import { collectDuctedLayoutSpaces } from 'utils/ducted-layout-configuration';
import { getPlacedExistingEquipmentComponents } from 'utils/existing-equipment';

type HybridWholeHouseConfigurationWatcherProps = {
  loading: boolean;
};

// While the 'hybrid-whole-house' System Design bypass is on (Hybrid with no
// mini-split rooms — utils/system-design-bypass), the configurations slice
// must hold the whole-house ducted configuration that stands in for System
// Design. Re-syncs whenever the inputs it is built from change (the rooms,
// the placed existing equipment, the chosen condenser side) or the
// configurations change underneath it (ResetDataWatcher wipes them on every
// space-set / preference change — including the room add that turns the
// mode off, so nothing here needs tearing down).
export default function HybridWholeHouseConfigurationWatcher({
  loading
}: HybridWholeHouseConfigurationWatcherProps) {
  const dispatch = useAppDispatch();
  const bypassMode = useSystemDesignBypassMode();
  const configurations = useAppSelector(
    (state) => state.configurations.configurations
  );
  const layoutInputsKey = useAppSelector((state) => {
    const { structure, existingSystems, condenserLocations } = state.structure;
    return JSON.stringify({
      spaces: collectDuctedLayoutSpaces(structure).map(({ space, level }) => [
        level.uniqueId,
        space.uniqueId
      ]),
      equipment: getPlacedExistingEquipmentComponents(existingSystems).map(
        (component) => [
          component.uniqueId,
          component.levelUniqueId,
          component.position
        ]
      ),
      condenserLocations
    });
  });

  useEffect(() => {
    if (loading || bypassMode !== 'hybrid-whole-house') return;
    dispatch(syncHybridWholeHouseConfiguration());
  }, [loading, bypassMode, layoutInputsKey, configurations, dispatch]);

  return null;
}
